import { useEffect, useState } from 'react';
import type { WidgetProps } from '@frontend/widgets/types';
import { fetchWidgetData } from '../api';

interface BugsPayload {
	total: number;
	open: number;
	closed: number;
	overdue: number;
	statusDistribution: Array<{ status: string; count: number }>;
	items: Array<{
		id: number;
		subject: string;
		status: string;
		dueDate: string | null;
		overdue: boolean;
		updatedAt: string | null;
		href: string | null;
	}>;
}

export default function OpenProjectBugsIssuesWidget({
	contextType,
	contextId,
	ecosystemId,
}: WidgetProps) {
	const [data, setData] = useState<BugsPayload | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	useEffect(() => {
		if (contextType !== 'project') {
			setError('Bugs and issues analytics are available on project views only.');
			setLoading(false);
			return;
		}
		setLoading(true);
		setError('');
		fetchWidgetData<BugsPayload>('openproject-bugs-issues', {
			ecosystemId,
			contextType,
			contextId,
		})
			.then((d) => setData(d))
			.catch((err: Error) => setError(err.message))
			.finally(() => setLoading(false));
	}, [contextType, contextId, ecosystemId]);

	if (loading) return <div className="widget-inner-loading">Loading bugs…</div>;
	if (error) return <div className="widget-inner-error">{error}</div>;
	if (!data || data.total === 0) {
		return <div className="widget-inner-empty">No bugs or issues found for this project.</div>;
	}

	return (
		<div className="widget-bugs-issues">
			<div className="tree-dashboard-metrics">
				<div className="tree-dashboard-metric-card">
					<div className="tree-dashboard-metric-label">Open</div>
					<div className="tree-dashboard-metric-value">{data.open}</div>
				</div>
				<div className="tree-dashboard-metric-card">
					<div className="tree-dashboard-metric-label">Overdue</div>
					<div className="tree-dashboard-metric-value">{data.overdue}</div>
				</div>
				<div className="tree-dashboard-metric-card">
					<div className="tree-dashboard-metric-label">Closed</div>
					<div className="tree-dashboard-metric-value">{data.closed}</div>
				</div>
			</div>

			{data.statusDistribution.length > 0 ? (
				<div className="widget-status-distribution">
					{data.statusDistribution.map((entry) => (
						<div key={entry.status} className="widget-status-row">
							<span>{entry.status}</span>
							<strong>{entry.count}</strong>
						</div>
					))}
				</div>
			) : null}

			<ul className="widget-list">
				{data.items.map((item) => (
					<li key={item.id} className="widget-list-item">
						<div className="widget-list-primary">
							{item.href ? (
								<a href={item.href} target="_blank" rel="noopener noreferrer">
									{item.subject}
								</a>
							) : (
								<span>{item.subject}</span>
							)}
						</div>
						<div className="widget-list-meta">
							<span className="widget-status-badge">{item.status}</span>
							{item.overdue ? <span className="widget-overdue">Overdue</span> : null}
							{item.dueDate ? <span>Due {new Date(item.dueDate).toLocaleDateString()}</span> : null}
						</div>
					</li>
				))}
			</ul>
		</div>
	);
}
